<script setup lang="ts">
import { EMBED_LAYOUT_TYPES, type EmbedSettings } from "@/util/embedCode";
import { useAppStore } from "@/store/app";
import {
  BrowserIcon,
  Code02Icon,
  FilterFunnel02Icon,
  XCloseIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UITag, UITooltip } from "@gohighlevel/ghl-ui";
import { computed, onMounted, onUnmounted, ref, shallowRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import BrowserFrame from "./BrowserFrame.vue";
import RealFormPreview from "./RealFormPreview.vue";

const props = defineProps<{ settings: EmbedSettings }>();
const emit = defineEmits<{ (event: "open-code"): void }>();
const store = useAppStore();
const { t } = useI18n();
const canvas = shallowRef<HTMLElement | null>(null);
const canvasWidth = ref(0);
const DESKTOP_REFERENCE_VIEWPORT_WIDTH = 1440;
const launcherDimensions = {
  small: { textHeight: 36, textPadding: 14, sidebarWidth: 36, sidebarPaddingY: 12, sidebarPaddingX: 8, square: 40, glyph: 18 },
  medium: { textHeight: 40, textPadding: 16, sidebarWidth: 40, sidebarPaddingY: 16, sidebarPaddingX: 10, square: 48, glyph: 20 },
  large: { textHeight: 44, textPadding: 18, sidebarWidth: 44, sidebarPaddingY: 20, sidebarPaddingX: 12, square: 56, glyph: 24 },
} as const;
let canvasObserver: ResizeObserver | undefined;
const measureCanvas = () => {
  canvasWidth.value = canvas.value?.getBoundingClientRect().width ?? 0;
};
onMounted(() => {
  measureCanvas();
  if (canvas.value && typeof ResizeObserver !== "undefined") {
    canvasObserver = new ResizeObserver(measureCanvas);
    canvasObserver.observe(canvas.value);
  }
});
onUnmounted(() => canvasObserver?.disconnect());
type UpcomingDestination = "funnels" | "websites";
const hoveredDestination = ref<UpcomingDestination | null>(null);
const focusedDestination = ref<UpcomingDestination | null>(null);
const showDestinationTooltip = (destination: UpcomingDestination) =>
  hoveredDestination.value === destination ||
  focusedDestination.value === destination;
const shouldStartOpen = () =>
  props.settings.layout.id === "INLINE" ||
  (["STICKY_SIDEBAR", "POLITE_SLIDE_IN"].includes(props.settings.layout.id) &&
    !props.settings.layout.allowMinimize);
const open = ref(shouldStartOpen());
const showLauncher = computed(
  () =>
    props.settings.layout.id === "POPUP" ||
    (["STICKY_SIDEBAR", "POLITE_SLIDE_IN"].includes(props.settings.layout.id) &&
      props.settings.layout.allowMinimize)
);
const layoutName = computed(() => {
  return (
    EMBED_LAYOUT_TYPES.find(({ id }) => id === props.settings.layout.id)?.key ??
    "standard"
  );
});
const layoutLabel = computed(() => {
  if (layoutName.value === "sidebar") return t("integrate.embed.sidebarLower");
  if (layoutName.value === "popover") return t("integrate.embed.popoverLower");
  if (layoutName.value === "popup") return t("integrate.embed.popupLower");
  return t("integrate.embed.standardLower");
});
const launcherStyle = computed(() => {
  const scale = canvasWidth.value
    ? canvasWidth.value / DESKTOP_REFERENCE_VIEWPORT_WIDTH
    : 1;
  const dimensions = launcherDimensions[props.settings.layout.buttonSize || "medium"];
  const scaled = (pixels: number) => `${Number((pixels * scale).toFixed(2))}px`;
  const corners = props.settings.layout.cornerRadius;
  const tl = corners?.tl ?? 8;
  const tr = corners?.tr ?? 8;
  const br = corners?.br ?? 8;
  const bl = corners?.bl ?? 8;
  let borderRadius = `${scaled(tl)} ${scaled(tr)} ${scaled(br)} ${scaled(bl)}`;
  if (props.settings.layout.id === "STICKY_SIDEBAR") {
    borderRadius = props.settings.layout.isLeftAligned
      ? `0px ${scaled(tr)} ${scaled(br)} 0px`
      : `${scaled(tl)} 0px 0px ${scaled(bl)}`;
  }
  return {
    "--launcher-color": props.settings.layout.buttonColor || "#101828",
    "--launcher-text-height": scaled(dimensions.textHeight),
    "--launcher-text-padding": scaled(dimensions.textPadding),
    "--launcher-sidebar-width": scaled(dimensions.sidebarWidth),
    "--launcher-sidebar-min-height": scaled(104),
    "--launcher-sidebar-padding-y": scaled(dimensions.sidebarPaddingY),
    "--launcher-sidebar-padding-x": scaled(dimensions.sidebarPaddingX),
    "--launcher-square": scaled(dimensions.square),
    "--launcher-glyph": scaled(dimensions.glyph),
    "--launcher-content-gap": scaled(8),
    borderRadius,
    fontSize: scaled(props.settings.layout.fontSize || 16),
  };
});
const launcherLabel = computed(
  () => props.settings.layout.minimizedTitle ?? ""
);
const launcherIcon = computed(() => props.settings.layout.buttonIcon || "chat");
const launcherButtonType = computed(
  () => props.settings.layout.buttonType || "text"
);
const launcherHasIcon = computed(() =>
  ["icon", "textIcon"].includes(launcherButtonType.value)
);
const launcherHasLabel = computed(() => launcherButtonType.value !== "icon");
const alignmentClass = computed(() => {
  if (!["STICKY_SIDEBAR", "POLITE_SLIDE_IN"].includes(props.settings.layout.id))
    return "";
  return props.settings.layout.isLeftAligned
    ? "embed-preview__launcher--left"
    : "embed-preview__launcher--right";
});
const showHint = computed(
  () =>
    layoutName.value !== "standard" &&
    !(layoutName.value === "popup" && open.value)
);
watch(
  () => [props.settings.layout.id, props.settings.layout.allowMinimize],
  () => {
    open.value = shouldStartOpen();
  }
);
</script>

<template>
  <section
    data-testid="embed-preview"
    class="embed-preview"
    :class="{ 'embed-preview--mobile': store.deviceMode === 'mobile' }"
  >
    <header class="embed-preview__header">
      <h2 data-testid="embed-preview-heading">
        {{ t("integrate.embed.embedIn") }}
      </h2>
      <div class="embed-preview__header-actions">
        <UITooltip
          trigger="manual"
          placement="top"
          :show="showDestinationTooltip('funnels')"
        >
          <template #trigger>
            <span
              data-testid="embed-funnels-tooltip-trigger"
              class="embed-preview__destination-trigger"
              role="group"
              tabindex="0"
              :aria-label="`${t('integrate.embed.funnels')}, ${t('common.comingSoon')}`"
              @mouseenter="hoveredDestination = 'funnels'"
              @mouseleave="hoveredDestination = null"
              @focus="focusedDestination = 'funnels'"
              @blur="focusedDestination = null"
            >
              <UIButton
                data-testid="embed-funnels-button"
                class="embed-preview__destination-button"
                size="small"
                disabled
              >
                <FilterFunnel02Icon class="embed-preview__destination-icon" />
                <span class="embed-preview__destination-label">{{ t("integrate.embed.funnels") }}</span>
              </UIButton>
            </span>
          </template>
          {{ t("common.comingSoon") }}
        </UITooltip>
        <UITooltip
          trigger="manual"
          placement="top"
          :show="showDestinationTooltip('websites')"
        >
          <template #trigger>
            <span
              data-testid="embed-websites-tooltip-trigger"
              class="embed-preview__destination-trigger"
              role="group"
              tabindex="0"
              :aria-label="`${t('integrate.embed.websites')}, ${t('common.comingSoon')}`"
              @mouseenter="hoveredDestination = 'websites'"
              @mouseleave="hoveredDestination = null"
              @focus="focusedDestination = 'websites'"
              @blur="focusedDestination = null"
            >
              <UIButton
                data-testid="embed-websites-button"
                class="embed-preview__destination-button"
                size="small"
                disabled
              >
                <BrowserIcon class="embed-preview__destination-icon" />
                <span class="embed-preview__destination-label">{{ t("integrate.embed.websites") }}</span>
              </UIButton>
            </span>
          </template>
          {{ t("common.comingSoon") }}
        </UITooltip>
        <div class="embed-preview__code-group">
          <span class="embed-preview__header-divider" aria-hidden="true" />
          <UITooltip trigger="hover" placement="top">
            <template #trigger>
              <UIButton
                id="open-embed-code"
                data-testid="open-embed-code"
                class="embed-preview__code-button"
                size="small"
                :aria-label="t('integrate.embed.embedCode')"
                @click="emit('open-code')"
              >
                <Code02Icon class="embed-preview__code-icon" />
                <span class="embed-preview__code-label">{{ t("integrate.embed.embedCode") }}</span>
              </UIButton>
            </template>
            {{ t("integrate.embed.embedCode") }}
          </UITooltip>
        </div>
      </div>
    </header>
    <div class="embed-preview__body embed-preview__body--scrollable">
      <UITag
        id="embed-preview-chip"
        data-testid="embed-preview-chip"
        class="embed-preview__chip integrate-preview-chip"
        type="info"
        size="small"
        :bordered="false"
      >
        {{ t("integrate.embed.preview") }}
      </UITag>
      <BrowserFrame>
        <div
          ref="canvas"
          class="embed-preview__canvas"
          :class="[
            `embed-preview__canvas--${layoutName}`,
            {
              'embed-preview__canvas--popup-open':
                layoutName === 'popup' && open,
            },
          ]"
        >
          <div
            v-if="layoutName !== 'standard'"
            class="embed-preview__page-lines"
            aria-hidden="true"
          >
            <span></span><span></span>
            <span class="embed-preview__page-block"></span>
          </div>
          <div
            v-else
            data-testid="embed-standard-hero"
            class="embed-preview__standard-hero"
            aria-hidden="true"
          >
            <span></span><span></span>
          </div>
          <RealFormPreview
            v-if="open"
            :layout="layoutName"
            :form-width="settings.layout.formWidth ?? store.formStyle.width"
            :form-height="settings.layout.formHeight"
            class="embed-preview__form"
            :class="[
              `embed-preview__form--${layoutName}`,
              {
                'embed-preview__form--vertically-centered':
                  layoutName === 'sidebar' || layoutName === 'popup',
              },
              settings.layout.isLeftAligned
                ? 'embed-preview__form--left'
                : 'embed-preview__form--right',
            ]"
          >
            <template #controls>
              <UIButton
                id="embed-preview-close"
                v-if="settings.layout.id !== 'INLINE'"
                data-testid="embed-preview-close"
                class="embed-preview__close"
                :class="{ 'embed-preview__close--floating': ['popup', 'popover'].includes(layoutName) }"
                text
                :aria-label="t('common.close')"
                @click="open = false"
              >
                <XCloseIcon class="h-4 w-4" />
              </UIButton>
            </template>
          </RealFormPreview>
          <UIButton
            v-if="showLauncher && !open"
            id="embed-preview-launcher"
            type="primary"
            :size="settings.layout.buttonSize || 'medium'"
            :color="settings.layout.buttonColor || '#101828'"
            data-testid="embed-preview-launcher"
            class="embed-preview__launcher"
            :class="[
              `embed-preview__launcher--${layoutName}`,
              alignmentClass,
              {
                'embed-preview__launcher--icon-only':
                  launcherButtonType === 'icon',
              },
            ]"
            :style="launcherStyle"
            :aria-label="
              launcherButtonType === 'icon' ? launcherLabel : undefined
            "
            @click="open = true"
          >
            <span
              v-if="launcherHasIcon"
              data-testid="embed-preview-launcher-icon"
              :data-icon="launcherIcon"
              class="material-symbols-outlined embed-preview__launcher-icon"
              aria-hidden="true"
            >
              {{ launcherIcon }}
            </span>
            <span v-if="launcherHasLabel">{{ launcherLabel }}</span>
          </UIButton>
          <p
            v-if="showHint"
            data-testid="embed-preview-hint"
            class="embed-preview__hint"
          >
            <span aria-hidden="true">☝</span>
            {{ t("integrate.embed.previewHint", { type: layoutLabel }) }}
          </p>
        </div>
      </BrowserFrame>
    </div>
  </section>
</template>

<style scoped>
.embed-preview {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  border-radius: 12px;
  background: var(--base-white, #fff);
}
.embed-preview__header {
  container-type: inline-size;
  container-name: embed-header;
  display: flex;
  min-height: 58px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--gray-200, #eaecf0);
}
.embed-preview__header h2 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
}
.embed-preview__header-actions {
  display: flex;
  min-width: 0;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 8px;
}
.embed-preview__destination-trigger {
  display: inline-flex;
}
.embed-preview__destination-button {
  pointer-events: none;
}
.embed-preview__destination-button :deep(.n-button__content) {
  display: flex;
  align-items: center;
  gap: 8px;
}
.embed-preview__destination-icon {
  width: 16px;
  height: 16px;
}
.embed-preview__code-group {
  display: flex;
  align-items: center;
  gap: 12px;
}
.embed-preview__header-divider {
  width: 1px;
  height: 20px;
  background: var(--gray-300, #d0d5dd);
}
.embed-preview__code-button {
  --n-color: var(--base-white, #fff) !important;
  --n-color-hover: var(--primary-50, #eff4ff) !important;
  --n-color-focus: var(--primary-50, #eff4ff) !important;
  --n-color-pressed: var(--primary-50, #eff4ff) !important;
  --n-text-color: var(--primary-600, #155eef) !important;
  --n-text-color-hover: var(--primary-600, #155eef) !important;
  --n-text-color-focus: var(--primary-600, #155eef) !important;
  --n-text-color-pressed: var(--primary-600, #155eef) !important;
  --n-border: 1px solid var(--primary-300, #84adff) !important;
  --n-border-hover: 1px solid var(--primary-400, #528bff) !important;
  --n-border-focus: 1px solid var(--primary-400, #528bff) !important;
  --n-border-pressed: 1px solid var(--primary-400, #528bff) !important;
}
.embed-preview__code-button :deep(.n-button__content) {
  display: flex;
  align-items: center;
  gap: 8px;
}
.embed-preview__code-icon {
  width: 16px;
  height: 16px;
}
@container embed-header (max-width: 680px) {
  .embed-preview__destination-label,
  .embed-preview__code-label {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
  }
  .embed-preview__destination-trigger,
  .embed-preview__destination-button,
  .embed-preview__code-button {
    width: 36px;
    min-width: 36px;
  }
  .embed-preview__destination-button,
  .embed-preview__code-button {
    height: 36px;
    padding: 0 !important;
  }
  .embed-preview__destination-button :deep(.n-button__content),
  .embed-preview__code-button :deep(.n-button__content) {
    justify-content: center;
  }
}
.embed-preview__body {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;
  gap: 14px;
  padding: 16px 20px 20px;
}
.embed-preview__body--scrollable {
  overflow-y: auto;
}
.embed-preview__chip {
  align-self: flex-start;
}
.embed-preview :deep(.browser-frame) {
  max-width: none;
  min-height: 0;
  flex: 1;
}
.embed-preview--mobile :deep(.browser-frame) {
  width: min(100%, 390px);
  max-width: 390px;
  align-self: center;
}
.embed-preview :deep(.browser-frame__content) {
  min-height: 0;
  height: calc(100% - 40px);
  padding: 0;
}
.embed-preview__canvas {
  position: relative;
  display: flex;
  min-height: 440px;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--gray-50, #f9fafb);
}
.embed-preview__canvas--sidebar {
  overflow: visible;
}
.embed-preview__canvas--standard {
  justify-content: flex-start;
  overflow-y: auto;
  background: var(--base-white, #fff);
}
.embed-preview__standard-hero {
  display: flex;
  width: 100%;
  height: 35%;
  min-height: 150px;
  flex: 0 0 35%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: var(--gray-50, #f9fafb);
}
.embed-preview__standard-hero span {
  width: 42%;
  height: 12px;
  border-radius: 999px;
  background: var(--base-white, #fff);
}
.embed-preview__page-lines {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 62px;
  background: var(--gray-100, #f2f4f7);
}
.embed-preview__page-lines span {
  width: 42%;
  height: 14px;
  border-radius: 8px;
  background: #fff;
}
.embed-preview__page-lines .embed-preview__page-block {
  width: 76%;
  height: 58%;
  margin-top: 48px;
  border-radius: 8px;
  background: var(--gray-200, #eaecf0);
}
.embed-preview__canvas--popup-open::after {
  position: absolute;
  z-index: 1;
  inset: 0;
  background: rgb(16 24 40 / 18%);
  content: "";
}
.embed-preview__form {
  position: absolute;
  z-index: 2;
  display: flex;
  flex-direction: column;
  width: var(--preview-form-width);
  height: var(--preview-form-height);
  overflow: hidden;
  border-radius: 8px;
  background: var(--base-white, #fff);
  box-shadow: var(--shadow-lg);
}
.embed-preview__form--vertically-centered {
  top: 50%;
  transform: translateY(-50%);
}
.embed-preview__form--sidebar,
.embed-preview__form--popup,
.embed-preview__form--popover {
  max-height: 100%;
}
.embed-preview__form--sidebar {
  border-radius: 0;
  box-shadow: none;
}
.embed-preview__form--sidebar.embed-preview__form--left {
  left: 0;
  border-radius: 0;
  box-shadow: 10px 0 18px -12px rgb(16 24 40 / 28%);
}
.embed-preview__form--sidebar.embed-preview__form--right {
  right: 0;
  border-radius: 0;
  box-shadow: -10px 0 18px -12px rgb(16 24 40 / 28%);
}
.embed-preview__form--popover {
  right: 24px;
  bottom: 64px;
}
.embed-preview__form--popover.embed-preview__form--left {
  right: auto;
  left: 24px;
}
.embed-preview__form--popup {
  left: 50%;
  width: var(--preview-form-width);
  height: var(--preview-form-height);
  transform: translate(-50%, -50%);
}
.embed-preview__form--standard {
  position: relative;
  flex: 0 0 auto;
  margin-top: 16px;
  border: 1px solid var(--gray-200, #eaecf0);
  border-radius: 8px;
  box-shadow: var(--shadow-sm);
}
.embed-preview__form--standard :deep(.real-form-preview__ghost-content) {
  height: auto;
}
.embed-preview__form--standard :deep(.ghost-form-preview__banner) {
  aspect-ratio: 16 / 4.6;
  border-radius: 8px;
}
.embed-preview__form--fallback {
  gap: 10px;
  overflow-y: auto;
  padding: 40px 20px 20px;
}
.embed-preview__form--fallback.embed-preview__form--standard {
  padding: 16px;
}
.embed-preview__form--fallback.embed-preview__form--sidebar,
.embed-preview__form--fallback.embed-preview__form--popover {
  gap: 6px;
  padding: 36px 12px 12px;
}
.embed-preview__close {
  position: absolute;
  z-index: 3;
  top: 12px;
  right: 12px;
  width: 28px;
  min-width: 28px;
  height: 28px;
  padding: 0 !important;
  color: var(--gray-700, #344054) !important;
}
.embed-preview__close--floating {
  top: var(--preview-close-top);
  right: var(--preview-modal-inset);
  width: var(--preview-close-size);
  min-width: var(--preview-close-size);
  height: var(--preview-close-size) !important;
  border: var(--preview-close-border) solid #ccc !important;
  border-radius: 50% !important;
  background: #e8e8e8 !important;
}
.embed-preview__close--floating :deep(svg) {
  width: var(--preview-close-icon-size);
  height: var(--preview-close-icon-size);
}
.embed-preview__close--floating :deep(.n-button__border),
.embed-preview__close--floating :deep(.n-button__state-border) {
  display: none;
}
.embed-preview__launcher {
  position: absolute;
  z-index: 3;
  height: var(--launcher-text-height) !important;
  min-height: var(--launcher-text-height);
  padding: 0 var(--launcher-text-padding) !important;
  white-space: nowrap;
  border-color: var(--launcher-color) !important;
  background: var(--launcher-color) !important;
  color: var(--base-white, #fff) !important;
}
.embed-preview__launcher :deep(.n-button__border),
.embed-preview__launcher :deep(.n-button__state-border) {
  display: none;
}
.embed-preview__launcher :deep(.n-button__content) {
  display: flex;
  align-items: center;
  gap: var(--launcher-content-gap);
}
.embed-preview__launcher-icon {
  display: inline-flex;
  width: var(--launcher-glyph);
  height: var(--launcher-glyph);
  align-items: center;
  justify-content: center;
  font-size: var(--launcher-glyph);
}
.embed-preview__launcher--sidebar {
  width: var(--launcher-sidebar-width);
  height: auto !important;
  min-height: var(--launcher-sidebar-min-height);
  padding: var(--launcher-sidebar-padding-y) var(--launcher-sidebar-padding-x) !important;
  top: 50%;
  transform: translateY(-50%);
  writing-mode: vertical-rl;
}
.embed-preview__launcher--sidebar.embed-preview__launcher--left {
  box-shadow: 6px 0 12px -8px rgb(16 24 40 / 45%);
}
.embed-preview__launcher--sidebar.embed-preview__launcher--right {
  box-shadow: -6px 0 12px -8px rgb(16 24 40 / 45%);
}
.embed-preview__launcher--popover {
  bottom: 24px;
}
.embed-preview__launcher--icon-only {
  width: var(--launcher-square) !important;
  min-width: var(--launcher-square) !important;
  height: var(--launcher-square) !important;
  min-height: var(--launcher-square) !important;
  aspect-ratio: 1;
  padding: 0 !important;
  writing-mode: horizontal-tb;
}
.embed-preview__launcher--popover .embed-preview__launcher-icon {
  width: var(--launcher-glyph);
  height: var(--launcher-glyph);
  font-size: var(--launcher-glyph);
}
.embed-preview__launcher--popup {
  bottom: 54px;
  left: 50%;
  transform: translateX(-50%);
}
.embed-preview__launcher--left {
  left: 0;
}
.embed-preview__launcher--right {
  right: 0;
}
.embed-preview__launcher--popover.embed-preview__launcher--left {
  left: 24px;
}
.embed-preview__launcher--popover.embed-preview__launcher--right {
  right: 24px;
}
.embed-preview__hint {
  position: absolute;
  z-index: 1;
  bottom: 18px;
  left: 50%;
  width: max-content;
  margin: 0;
  transform: translateX(-50%);
  color: var(--gray-700, #344054);
  font-size: 12px;
}
.embed-preview__hint > span {
  margin-right: 6px;
  color: #f79009;
}
.embed-preview--mobile
  .embed-preview__launcher--popover
  .embed-preview__launcher-icon {
  width: 20px;
  height: 20px;
  font-size: 20px;
}
</style>
